import React, { Fragment, useEffect } from 'react';
import { isFunction } from 'lodash';
import { autorun } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import { Grid, Card, CardHeader, CardContent, Typography, Box } from '@material-ui/core';
import { anketsStore, accountingStore } from 'stores';
import { Page, AppBar, DataGrid, Column, EmptyState, ScreenLoader } from 'components';
import { amountFormatter } from 'utils/formatters';
import { ROW_HEIGHT } from 'utils/constants';
import { ru } from 'locale';
import { NoData as NoDataBg } from 'resources/images';
import { initialColumns, accountingSections } from './AccountingScreen.mock';
import useStyles from './AccountingScreen.styles';

const AccountingScreen = observer(() => {
  const { reports, fetchState, allAccountCodes, accountingYears } = accountingStore;
  const classes = useStyles();
  const history = useHistory();
  const hasReport = reports.length !== 0;

  useEffect(
    () =>
      autorun(() => {
        accountingStore.getAccounting(anketsStore.anketId);
      }),
    [],
  );

  const handleBackClick = () => {
    history.goBack();
  };

  const createColumns = () => {
    return accountingYears.reduce((acc, accountingYear) => {
      const column = {
        dataKey: accountingYear,
        label: accountingYear,
        width: 130,
        formatter: amountFormatter,
      };

      return [...acc, column];
    }, initialColumns);
  };

  const createTableDataFromAccountCodes = accountCodes => {
    return accountCodes.map(accountCode => {
      const row = allAccountCodes[accountCode];

      return row;
    });
  };

  const cellDataGetter = ({ dataKey, rowData, formatter }) => {
    const value = rowData[dataKey] || 0;

    if (isFunction(formatter)) {
      return formatter(value);
    }

    return value;
  };

  const cellRenderer = ({ rowCount, cellData, rowIndex, columnIndex }) => {
    if (columnIndex === 1) {
      return <span className={classes.code}>{cellData}</span>;
    }

    if (rowIndex === rowCount) {
      return <span className={classes.total}>{cellData}</span>;
    }

    return cellData;
  };

  const renderTables = tables => {
    const columns = createColumns();
    const columnCount = columns.length;

    return tables.map(table => {
      const data = createTableDataFromAccountCodes(table.codes);
      const dataCount = data.length;
      const rowCount = dataCount + 1;
      const height = rowCount * ROW_HEIGHT;

      return (
        <Fragment key={table.headerName}>
          <Box p={2}>
            <Typography variant="button" classes={{ h6: classes.accountingCategoryTitle }}>
              {table.headerName}
            </Typography>
            <div className={classes.justGrid}>
              <DataGrid
                dataRowCount={dataCount}
                fixedColumnCount={1}
                height={height}
                rowGetter={({ index }) => data[index]}
              >
                {columns.map(({ dataKey, ...other }) => {
                  return (
                    <Column
                      key={dataKey}
                      dataKey={dataKey}
                      cellDataGetter={cellDataGetter}
                      cellRenderer={({ cellData, rowIndex, columnIndex }) => {
                        return cellRenderer({
                          rowCount: dataCount,
                          columnCount,
                          cellData,
                          rowIndex,
                          columnIndex,
                        });
                      }}
                      {...other}
                    />
                  );
                })}
              </DataGrid>
            </div>
          </Box>
        </Fragment>
      );
    });
  };

  const renderAccounting = () => {
    if (!hasReport) {
      return (
        <EmptyState
          image={{ source: NoDataBg, alt: ru.emptyDebtorAnalytics }}
          title={ru.emptyDebtorAnalytics}
        />
      );
    }

    return (
      <Grid item xs={12}>
        {accountingSections.map(({ name, tables }) => {
          return (
            <Box key={name} my={2}>
              <Card square>
                <CardHeader title={name} />
                <CardContent classes={{ root: classes.cardContent }}>
                  {renderTables(tables)}
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Grid>
    );
  };

  return (
    <Page fullScreen style={{ minHeight: '100rvh' }} className={classes.root}>
      <Grid container direction="column">
        <AppBar title={ru.accounting} onNavButtonClick={handleBackClick} />
        {fetchState === 'pending' && <ScreenLoader />}

        <Grid
          container
          item
          direction="column"
          justify={hasReport ? 'flex-start' : 'center'}
          className={cn({
            [classes.content]: !hasReport,
          })}
        >
          {fetchState === 'fulfilled' && renderAccounting()}
        </Grid>
      </Grid>
    </Page>
  );
});

export default AccountingScreen;
